/**
 * @copyright
 * (c) 2026 netTrek GmbH & Co. KG
 *
 * C2PA BMFF content hashing (`c2pa.hash.bmff` v2/v3), ported from the c2pa-rs
 * algorithm (Apache-2.0 reference). The hash covers the asset's bytes with the
 * assertion's box exclusions removed; in BMFF-V2 mode each *included* top-level
 * box is preceded by its 8-byte big-endian file offset (a "BMFF V2 offset"
 * marker), and `mdat` payloads exclude the leading bytes.
 */

import { buildBmffTree, type TreeBox } from './bmffTree'

/** A box-content constraint: the bytes at `offset` within the box must equal `value`. */
export interface BmffConstraint {
    offset: number
    value: number[] | Uint8Array
}

/** A C2PA BMFF hash exclusion entry (the assertion's `exclusions` map). */
export interface BmffExclusion {
    xpath: string
    data?: BmffConstraint[]
    /** Partial-box subset ranges to exclude (offset/length within the box). */
    subset?: Array<{ offset: number, length: number }>
    /** Only apply if the box's total size equals this. */
    length?: number | null
    /** Only apply if the box's FullBox version equals this. */
    version?: number | null
    /** Only apply if the box's FullBox flags match (3 bytes, big-endian). */
    flags?: number[] | Uint8Array | null
    /** With `flags`: exact match (default true) vs bitwise subset (`desired | box == desired`). */
    exact?: boolean | null
}

export interface BmffHashOptions {
    exclusions?: BmffExclusion[]
    /** WebCrypto digest name, e.g. `SHA-256`. */
    alg?: string
    /** BMFF-V2 offset-prefix mode (true for v2/v3). */
    bmffV2?: boolean
}

function constraintMatches (u8: Uint8Array, box: TreeBox, c: BmffConstraint): boolean {
    const value = c.value instanceof Uint8Array ? c.value : new Uint8Array(c.value)
    const at = box.start + c.offset
    if (at + value.length > box.end) return false
    for (let i = 0; i < value.length; i++) {
        if (u8[at + i] !== value[i]) return false
    }
    return true
}

/** Interprets the 3-byte flags qualifier as a 24-bit big-endian value (c2pa-rs uses the low 3 bytes). */
function flagsValue (flags: number[] | Uint8Array): number {
    const b = flags instanceof Uint8Array ? flags : new Uint8Array(flags)
    return b.length >= 3 ? (b[0] << 16) | (b[1] << 8) | b[2] : 0
}

/**
 * Whether an exclusion's qualifiers (`length`/`version`/`flags`/`exact`/`data`)
 * match a resolved box. Mirrors c2pa-rs `bmff_to_jumbf_exclusions` — a
 * version/flags qualifier is skipped (treated as matching) when the box carries
 * no such header.
 */
function qualifiersMatch (u8: Uint8Array, box: TreeBox, ex: BmffExclusion): boolean {
    if (ex.length != null && ex.length !== box.size) return false
    if (ex.version != null && box.version != null && ex.version !== box.version) return false
    if (ex.flags != null && box.flags != null) {
        const desired = flagsValue(ex.flags)
        const exact = ex.exact ?? true
        if (exact ? desired !== box.flags : (desired | box.flags) !== desired) return false
    }
    if (ex.data && ex.data.length > 0 && !ex.data.every(c => constraintMatches(u8, box, c))) return false
    return true
}

/** Subtracts the exclusion `[start,end)` ranges from `[0,dataLen)`, returning sorted included ranges. */
function includedRanges (dataLen: number, exclusions: Array<[number, number]>): Array<[number, number]> {
    const sorted = exclusions.filter(([s, e]) => e > s).sort((a, b) => a[0] - b[0])
    const included: Array<[number, number]> = []
    let cursor = 0
    for (const [s, e] of sorted) {
        const start = Math.max(s, cursor)
        if (start > cursor) included.push([cursor, start])
        cursor = Math.max(cursor, e)
    }
    if (cursor < dataLen) included.push([cursor, dataLen])
    return included
}

function be64 (n: number): Uint8Array {
    const b = new Uint8Array(8)
    new DataView(b.buffer).setBigUint64(0, BigInt(n), false)
    return b
}

function concat (parts: Uint8Array[]): Uint8Array {
    let len = 0
    for (const p of parts) len += p.length
    const out = new Uint8Array(len)
    let off = 0
    for (const p of parts) { out.set(p, off); off += p.length }
    return out
}

/**
 * Builds the exact byte sequence c2pa-rs feeds to the digest (§18.6). The full
 * box tree is walked so exclusions can target nested paths
 * (`/moov/trak/mdia/minf/stbl/stco`) with `subset`/`version`/`flags`/`length`
 * qualifiers; each exclusion resolves to concrete byte ranges removed from the
 * asset. In BMFF-V2 mode each *included* top-level box is preceded by its 8-byte
 * big-endian file offset. Mirrors `bmff_to_jumbf_exclusions` +
 * `hash_stream_by_alg` (Apache-2.0 reference).
 */
export function buildBmffHashInput (u8: Uint8Array, options: BmffHashOptions = {}): Uint8Array {
    const exclusions = options.exclusions ?? []
    const bmffV2 = options.bmffV2 ?? true
    const tree = buildBmffTree(u8)

    // Resolve exclusions to concrete byte ranges. A whole-box (no-subset)
    // exclusion of a top-level box also drops that box's V2 offset marker.
    const exclRanges: Array<[number, number]> = []
    const topLevelOffsets = new Set(tree.topLevelOffsets)
    for (const ex of exclusions) {
        if (!ex.xpath) continue
        const boxes = tree.boxesByPath.get(ex.xpath)
        if (!boxes) continue
        for (const box of boxes) {
            if (!qualifiersMatch(u8, box, ex)) continue
            if (ex.subset && ex.subset.length > 0) {
                for (const s of ex.subset) {
                    if (s.offset > box.size) continue
                    const start = box.start + s.offset
                    const len = s.length === 0 ? box.size - s.offset : Math.min(s.length, box.size - s.offset)
                    exclRanges.push([start, start + len])
                }
            } else {
                exclRanges.push([box.start, box.end])
                topLevelOffsets.delete(box.start)
            }
        }
    }

    const included = includedRanges(u8.byteLength, exclRanges)
    const markers = bmffV2 ? [...topLevelOffsets].sort((a, b) => a - b) : []
    const parts: Uint8Array[] = []
    if (included.length === 0) return concat(parts)

    // Emit the included byte ranges, inserting each surviving top-level box's 8-byte
    // offset marker immediately before that box's first surviving byte — in file
    // order. A marker whose box start falls inside an included range splits it; a
    // marker whose box start was removed by a *subset* exclusion (so it lands in an
    // excluded gap) is still emitted, positioned where the box begins, matching
    // c2pa-rs (BMFF-4). Mirrors the reference's strict interior guard: a gap marker
    // only survives when firstStart < os < lastEnd.
    const firstStart = included[0][0]
    const lastEnd = included[included.length - 1][1]
    let mi = 0
    for (const [start, end] of included) {
        let cursor = start
        while (mi < markers.length && markers[mi] < end) {
            const os = markers[mi]
            if (os < start) {
                // Box start in an excluded gap before this range (subset removed its head).
                if (os > firstStart && os < lastEnd) parts.push(be64(os))
                mi++
                continue
            }
            // Box start inside this range → split the range at the marker.
            if (os > cursor) parts.push(u8.subarray(cursor, os))
            parts.push(be64(os))
            cursor = os
            mi++
        }
        if (cursor < end) parts.push(u8.subarray(cursor, end))
    }
    return concat(parts)
}

/**
 * Validates the `subset` ranges of every exclusion (§15.12.2 l.4128, BMFF-2): when
 * a box carries more than one subset they must be in ascending offset order and
 * non-overlapping (`subset[i].offset + subset[i].length` must not exceed
 * `subset[i+1].offset`); any negative offset/length is likewise malformed. A
 * violation means the manifest is rejected with `assertion.bmffHash.malformed`
 * rather than applying out-of-order/overlapping/negative ranges. Returns false when
 * malformed. Mirrors c2pa-rs `BmffHash::verify_self`.
 */
export function subsetsAreWellFormed (exclusions: BmffExclusion[]): boolean {
    for (const ex of exclusions) {
        const subs = ex.subset
        if (!subs || subs.length === 0) continue
        for (const s of subs) {
            if (!(s.offset >= 0) || !(s.length >= 0)) return false // negative or NaN
        }
        for (let i = 0; i + 1 < subs.length; i++) {
            if (subs[i].offset > subs[i + 1].offset) return false // not ascending
            if (subs[i].offset + subs[i].length > subs[i + 1].offset) return false // overlap
        }
    }
    return true
}

/** Computes the C2PA BMFF content hash. `alg` is a WebCrypto digest name. */
export async function computeBmffHash (u8: Uint8Array, options: BmffHashOptions = {}): Promise<Uint8Array> {
    const input = buildBmffHashInput(u8, options)
    const digest = await crypto.subtle.digest(options.alg ?? 'SHA-256', input as unknown as ArrayBuffer)
    return new Uint8Array(digest)
}

/**
 * Maps a C2PA algorithm string (§13.1: `sha256`/`sha384`/`sha512`) to a WebCrypto
 * digest name, or `null` for an absent/unsupported algorithm. Per §13.1 the allowed
 * list is exactly those three (deprecated list empty), and §15.4.4 requires rejecting
 * anything else with `algorithm.unsupported`. There is deliberately **no** default —
 * a silent SHA-256 fallback would let a manifest declaring a bogus alg (but storing
 * SHA-256 hashes) validate.
 */
export function webcryptoDigestForC2pa (alg: string | undefined): string | null {
    switch ((alg ?? '').toLowerCase()) {
        case 'sha256': return 'SHA-256'
        case 'sha384': return 'SHA-384'
        case 'sha512': return 'SHA-512'
        default: return null
    }
}
